import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, ListGroup, Image, Form, Alert, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { getCastaways } from '../api/castaways';
import { Castaway, League, Member, Draft } from '../types';
import TribeBadge, { getTribeColor } from './TribeBadge';
import '../App.css';

interface LeagueDrafts {
  [userName: string]: Draft;
}

const AdminDraftEdit: React.FC = () => {
  const [allCastaways, setAllCastaways] = useState<Castaway[]>([]);
  const [leagues, setLeagues] = useState<League[]>([]);
  const [selectedLeagueId, setSelectedLeagueId] = useState<string>('');
  const [selectedUserName, setSelectedUserName] = useState<string>('');
  const [members, setMembers] = useState<Member[]>([]);
  const [drafts, setDrafts] = useState<{ [leagueId: string]: LeagueDrafts }>({});
  const [myTeam, setMyTeam] = useState<Castaway[]>([]);
  const [availableCastaways, setAvailableCastaways] = useState<Castaway[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedSeason, setSelectedSeason] = useState<number>(() => {
    const saved = localStorage.getItem('adminSelectedSeason');
    return saved ? parseInt(saved) : 49;
  });

  const selectedLeague = leagues.find(l => l.id === selectedLeagueId);
  const maxTeamSize = selectedLeague?.rosterSize || (selectedSeason >= 51 ? 5 : 3);

  const fetchLeagues = useCallback(async (season: number) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/leagues/all`);
      if (!response.ok) throw new Error('Failed to fetch leagues');
      const data = await response.json();
      const filteredLeagues = (data.leagues || []).filter((l: any) => (l.season || 49) === season);
      setLeagues(filteredLeagues);
      if (filteredLeagues.length > 0) {
        setSelectedLeagueId(filteredLeagues[0].id);
      } else {
        setSelectedLeagueId('');
        setMembers([]);
        setSelectedUserName('');
        setMyTeam([]);
      }
    } catch (err: any) {
      setError(err.message || 'Could not load leagues.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeagues(selectedSeason);

    const handleSeasonChange = () => {
      const newSeason = parseInt(localStorage.getItem('adminSelectedSeason') || '49');
      setSelectedSeason(newSeason);
      fetchLeagues(newSeason);
    };
    window.addEventListener('adminSeasonChanged', handleSeasonChange);
    return () => window.removeEventListener('adminSeasonChanged', handleSeasonChange);
  }, [fetchLeagues]);

  useEffect(() => {
    const loadCastaways = async () => {
      if (selectedLeagueId) {
        setIsLoading(true);
        try {
          const castaways = await getCastaways(selectedLeagueId);
          setAllCastaways(castaways);
          setAvailableCastaways(castaways);
        } catch (err) {
          setError('Could not load castaway data.');
        } finally {
          setIsLoading(false);
        }
      }
    };
    loadCastaways();
  }, [selectedLeagueId]);
  
  const getCastawayById = useCallback((id: number) => allCastaways.find(c => c.id === id), [allCastaways]);

  useEffect(() => {
    if (!selectedLeagueId) {
      setMembers([]);
      setSelectedUserName('');
      setDrafts({});
      return;
    }
    const league = leagues.find(l => l.id === selectedLeagueId);
    setMembers(league?.members || []);
    setSelectedUserName(league?.members[0]?.userName || '');

    const fetchDrafts = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${selectedLeagueId}/drafts`);
        if (!response.ok) throw new Error('Failed to fetch drafts');
        const data = await response.json();
        setDrafts(prev => ({ ...prev, [selectedLeagueId]: data.drafts || {} }));
      } catch (err: any) {
        setError(err.message || 'Could not load drafts.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchDrafts();
  }, [selectedLeagueId, leagues]);

  useEffect(() => {
    if (!selectedLeagueId || !selectedUserName || !drafts[selectedLeagueId]) {
      setMyTeam([]);
      setAvailableCastaways(allCastaways);
      return;
    }
    const userDraft: Draft | undefined = drafts[selectedLeagueId][selectedUserName];
    if (userDraft && userDraft.team) {
      const currentTeam = userDraft.team.map((id: number) => getCastawayById(id)).filter(Boolean) as Castaway[];
      setMyTeam(currentTeam);
      const draftedIds = new Set(userDraft.team);
      setAvailableCastaways(allCastaways.filter(c => !draftedIds.has(c.id)));
    } else {
      setMyTeam([]);
      setAvailableCastaways(allCastaways);
    }
  }, [selectedLeagueId, selectedUserName, drafts, allCastaways, getCastawayById]);

  const draftCastaway = (castaway: Castaway) => {
    if (myTeam.length < maxTeamSize) {
      setMyTeam([...myTeam, castaway]);
      setAvailableCastaways(availableCastaways.filter(c => c.id !== castaway.id));
    }
  };

  const removeCastaway = (castaway: Castaway) => {
    setMyTeam(myTeam.filter(c => c.id !== castaway.id));
    setAvailableCastaways([...availableCastaways, castaway].sort((a, b) => a.id - b.id));
  };

  const handleSaveChanges = async () => {
    if (!selectedLeagueId || !selectedUserName) {
      setError('Please select a league and user.');
      return;
    }
    if (myTeam.length !== maxTeamSize) {
      setError(`Team must have exactly ${maxTeamSize} castaways.`);
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);
    const teamIds = myTeam.map(c => c.id);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${selectedLeagueId}/draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userName: selectedUserName, team: teamIds }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to save draft');
      setSuccess(`Draft for ${selectedUserName} in ${leagues.find(l => l.id === selectedLeagueId)?.name} updated successfully!`);
       // Update local state to reflect changes
      setDrafts(prev => {
        const newDrafts = { ...prev };
        if (!newDrafts[selectedLeagueId]) newDrafts[selectedLeagueId] = {};
        newDrafts[selectedLeagueId][selectedUserName] = {
            ...(newDrafts[selectedLeagueId][selectedUserName] || { timestamp: '' }),
            team: teamIds,
            timestamp: new Date().toISOString(),
        };
        return newDrafts;
      });
    } catch (err: any) {
      setError(err.message || 'Error saving draft.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container className="mt-5 mb-5">
      <div className="mb-3">
        <Link to="/admin/dashboard" className="text-decoration-none">
          &larr; Back to Admin Home
        </Link>
      </div>
      <Card className="shadow-sm home-card">
        <Card.Header as="h4" className="card-title-custom text-center">Admin - Edit User Draft (Season {selectedSeason})</Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}
          {isLoading && <div className="text-center"><Spinner animation="border" /></div>}

          <Row className="mb-3">
            <Col md={6}>
              {leagues.length > 1 ? (
                <Form.Group controlId="leagueSelect">
                  <Form.Label>Select League:</Form.Label>
                  <Form.Select value={selectedLeagueId} onChange={e => setSelectedLeagueId(e.target.value)} disabled={isLoading}>
                    {leagues.map(league => (
                      <option key={league.id} value={league.id}>{league.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              ) : leagues.length === 1 ? (
                <div className="p-2 bg-light border rounded mt-4">
                  <strong>League:</strong> {leagues[0].name}
                </div>
              ) : (
                <Alert variant="info" className="mt-4">No leagues found for Season {selectedSeason}.</Alert>
              )}
            </Col>
            <Col md={6}>
              <Form.Group controlId="userSelect">
                <Form.Label>Select User:</Form.Label>
                <Form.Select value={selectedUserName} onChange={e => setSelectedUserName(e.target.value)} disabled={isLoading || members.length === 0}>
                  <option value="">-- Select User --</option>
                  {members.map(member => (
                    <option key={member.userName} value={member.userName}>{member.userName}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {selectedLeagueId && selectedUserName && (
            <Row>
              <Col md={7}>
                <Card className="mb-4">
                  <Card.Header>Available Castaways</Card.Header>
                  <ListGroup variant="flush" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {availableCastaways.map(castaway => (
                      <ListGroup.Item key={castaway.id} className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                          <Image
                            src={castaway.imageUrl}
                            roundedCircle
                            referrerPolicy="no-referrer"
                            width={40}
                            height={40}
                            className="me-2"
                            style={{
                              objectFit: 'cover',
                              border: `2px solid ${getTribeColor(castaway.tribe, selectedLeague?.tribes, castaway.tribeColor)}`
                            }}
                          />
                          <div>
                            <strong>{castaway.name}</strong>
                            <div className="d-flex align-items-center gap-1 mt-1">
                              <TribeBadge tribeName={castaway.tribe} tribes={selectedLeague?.tribes} tribeColor={castaway.tribeColor} style={{ fontSize: '0.65rem' }} />
                              <small className="text-muted">{castaway.occupation}</small>
                            </div>
                          </div>
                        </div>
                        <Button size="sm" variant="success" onClick={() => draftCastaway(castaway)} disabled={myTeam.length >= maxTeamSize || isLoading}>Draft</Button>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </Card>
              </Col>
              <Col md={5}>
                <Card>
                  <Card.Header>Team for {selectedUserName} ({myTeam.length}/{maxTeamSize})</Card.Header>
                  <ListGroup variant="flush">
                    {myTeam.map(castaway => (
                      <ListGroup.Item key={castaway.id} className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                          <Image
                            src={castaway.imageUrl}
                            roundedCircle
                            referrerPolicy="no-referrer"
                            width={30}
                            height={30}
                            className="me-2"
                            style={{
                              objectFit: 'cover',
                              border: `2px solid ${getTribeColor(castaway.tribe, selectedLeague?.tribes, castaway.tribeColor)}`
                            }}
                          />
                          <div>
                            <span>{castaway.name}</span>
                            <div className="mt-1">
                              <TribeBadge tribeName={castaway.tribe} tribes={selectedLeague?.tribes} tribeColor={castaway.tribeColor} style={{ fontSize: '0.65rem' }} />
                            </div>
                          </div>
                        </div>
                        <Button size="sm" variant="danger" onClick={() => removeCastaway(castaway)} disabled={isLoading}>Remove</Button>
                      </ListGroup.Item>
                    ))}
                    {myTeam.length === 0 && <ListGroup.Item>No selections yet.</ListGroup.Item>}
                  </ListGroup>
                </Card>
                <div className="text-center mt-3">
                  <Button onClick={handleSaveChanges} disabled={isLoading || myTeam.length !== maxTeamSize}>Save Changes for {selectedUserName}</Button>
                </div>
              </Col>
            </Row>
          )}
          <div className="text-center mt-4">
            <Link to="/admin/dashboard" className="btn btn-secondary">Back to Admin Dashboard</Link>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default AdminDraftEdit;